import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useToast } from '@/components/ui';
import { createOrgAssignment, OrgAdminApiError } from '@/api/orgAdmin';
import type { KeycloakAccountRow } from './types';

export interface BulkAssignOrgResultRow {
    sub: string;
    username: string;
    ok: boolean;
    error?: string;
}

/**
 * EAA-1820 (frame 75, "Gán nhiều tài khoản vào đơn vị", triggered from the
 * realm `/console/accounts` bulk bar): grants the SAME đơn vị + Level to every
 * selected identity in one operator action.
 *
 * WHY THIS IS N SEQUENTIAL CREATES, NOT ONE REQUEST. There is no bulk-create
 * endpoint on `organizations/{orgId}/assignments` — confirmed against
 * `AssignmentService`/`org_serializers.py`, which only exposes the single-row
 * `POST` this hook already uses for the realm's "Gán Level" dialog
 * (`useAssignLevelToAccount`). `Promise.allSettled` runs one `createOrgAssignment`
 * call per selected `user_sub` and every settlement is kept, mirroring the same
 * pattern `AccountsPage.tsx`'s `runBulkDelete`/`runBulkReset` already use for
 * their own N-calls-no-bulk-endpoint gaps.
 *
 * REAL OUTCOMES, NEVER AN OPTIMISTIC TOAST. A batch of 12 where 9 succeed and 3
 * fail (e.g. a sub already holds this exact Level, or the operator does not
 * manage the chosen đơn vị) reports 9/3, not a single success banner — the
 * failed rows stay selected afterwards so the operator can retry just those,
 * same convention as `runBulkDelete`/`runBulkReset`.
 *
 * ALSO A CREATE, NOT A MOVE. Same rule as `useAssignLevelToAccount`: an
 * identity already holding an ACTIVE placement elsewhere is not touched by
 * this call — it gains a SECOND grant. Moving an existing grant is the
 * per-row "Chuyển đơn vị" (frame 85, `useRowTransfer`), which needs an
 * assignment id this realm-wide, identity-only selection does not carry.
 */
export function useBulkAssignAccountsToOrg(invalidate: () => void, clearSelection: () => void) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const toast = useToast();

    const [targets, setTargets] = useState<KeycloakAccountRow[]>([]);
    const [orgId, setOrgId] = useState('');
    const [levelId, setLevelId] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [results, setResults] = useState<BulkAssignOrgResultRow[] | null>(null);

    const open = useCallback((rows: KeycloakAccountRow[]) => {
        setTargets(rows);
        setOrgId('');
        setLevelId('');
        setError(null);
        setResults(null);
    }, []);
    const close = useCallback(() => setTargets([]), []);
    const dismissResults = useCallback(() => setResults(null), []);

    /** Identities in `targets` that already hold an active placement elsewhere —
     *  surfaced so the operator knows this run ADDS a grant rather than moves one. */
    const alreadyPlacedCount = useMemo(
        () => targets.filter((row) => Boolean(row.placement)).length,
        [targets]
    );

    const canSubmit = Boolean(orgId) && Boolean(levelId) && targets.length > 0 && !busy;

    const submit = useCallback(async () => {
        if (!canSubmit) return;
        setError(null);
        setBusy(true);
        const settled = await Promise.allSettled(
            targets.map((row) =>
                createOrgAssignment(config.apiBase, accessToken as string, orgId, {
                    user_sub: row.sub,
                    ...(row.email ? { user_email: row.email } : {}),
                    level_id: levelId,
                })
            )
        );
        const rows: BulkAssignOrgResultRow[] = targets.map((row, i) => {
            const outcome = settled[i];
            if (outcome.status === 'fulfilled') {
                return { sub: row.sub, username: row.username, ok: true };
            }
            const message =
                outcome.reason instanceof OrgAdminApiError
                    ? outcome.reason.message
                    : t('console.accounts.bulkAssignOrg.errorFallback');
            return { sub: row.sub, username: row.username, ok: false, error: message };
        });
        setBusy(false);
        setTargets([]);
        setResults(rows);

        const failed = rows.filter((r) => !r.ok);
        if (failed.length === 0) {
            clearSelection();
            toast.success(t('console.accounts.bulkAssignOrg.successMessage', { count: rows.length }));
        } else {
            toast.error(
                t('console.accounts.bulkAssignOrg.partialMessage', {
                    succeeded: rows.length - failed.length,
                    total: rows.length,
                    failed: failed.length,
                })
            );
        }
        invalidate();
    }, [canSubmit, targets, orgId, levelId, config.apiBase, accessToken, invalidate, clearSelection, toast, t]);

    return {
        targets,
        open,
        close,
        orgId,
        setOrgId: (next: string) => {
            setOrgId(next);
            setLevelId(''); // EAA-1820: a Level belongs to exactly one org — changing the org invalidates the pick.
        },
        levelId,
        setLevelId,
        alreadyPlacedCount,
        error,
        busy,
        canSubmit,
        submit,
        results,
        dismissResults,
    };
}

export type BulkAssignAccountsToOrgVM = ReturnType<typeof useBulkAssignAccountsToOrg>;
